import { v4 as uuid4 } from "uuid";
import * as util from "./util";

export function toIterativeMenu(menuItems, depth = 0, parent = null) {
  let newMenu = [];
  menuItems.forEach((item) => {
    let newMenuItem = toIterativeMenuItem(item, depth, parent);
    newMenu.push(newMenuItem);

    let hasChildren = item.links.length !== 0;
    if (hasChildren) {
      newMenu = newMenu.concat(
        toIterativeMenu(item.links, depth + 1, newMenuItem.key)
      );
    }
  });
  return newMenu;
}

function toIterativeMenuItem(menuItem, depth, parent) {
  let hasChildren = menuItem.links.length !== 0;
  let newItem = JSON.parse(JSON.stringify(menuItem));
  if (!newItem.key) {
    newItem.key = uuid4().toString();
  }
  delete newItem.links;
  newItem.depth = depth;
  newItem.parent = parent;
  newItem.hasChildren = hasChildren;
  return newItem;
}

export function toRecursiveMenu(menuItems) {
  let menuItemsGrouped = util.groupBy(menuItems, "parent");

  let newMenu = [];
  menuItemsGrouped.null.forEach((item) => {
    newMenu.push(toRecursiveMenuItem(item, menuItemsGrouped));
  });
  return newMenu;
}

function toRecursiveMenuItem(menuItem, menuItemsGrouped) {
  let newItem = JSON.parse(JSON.stringify(menuItem));
  newItem.links = [];
  if (menuItem.hasChildren) {
    menuItemsGrouped[menuItem.key].forEach((item) => {
      newItem.links.push(toRecursiveMenuItem(item, menuItemsGrouped));
    });
  }
  delete newItem.depth;
  delete newItem.parent;
  delete newItem.hasChildren;
  return newItem;
}

/*
  Iterative START
*/
export function removeMenuItem(items, key, keepChildren) {
  let index = items.findIndex((item) => key === item.key);
  if (index === -1) {
    return items;
  }

  let parentKey = items[index].parent;
  let newItems = JSON.parse(JSON.stringify(items));
  newItems.splice(index, 1);

  if (keepChildren) {
    // Assign children of removed item to that item's parent
    newItems.forEach((item, i) => {
      if (item.parent === key) {
        newItems[i].parent = parentKey;
      }
    });
  } else {
    // Remove the children of removed item
    let childrenToRemove = getChildKeys(newItems, key, false);
    childrenToRemove.forEach((toRemove) => {
      let indexToRemove = newItems.findIndex((item) => toRemove === item.key);
      if (indexToRemove !== -1) {
        newItems.splice(indexToRemove, 1);
      }
    });
  }

  // Update hasChildren on parent appropriately
  if (parentKey !== null && numberOfChildren(newItems, parentKey) === 0) {
    let parentIndex = newItems.findIndex((item) => parentKey === item.key);
    newItems[parentIndex].hasChildren = false;
  }

  return newItems;
}

export function addMenuItem(items, parentKey = null, menuItem = null) {
  let depth = 0;
  let parentIndex = items.length;
  if (parentKey !== null) {
    parentIndex = items.findIndex((item) => parentKey === item.key);
    if (parentIndex !== -1) {
      depth = items[parentIndex].depth + 1;
    }
  }
  let menuItemToAdd = menuItem ?? newMenuItem(depth, parentKey);
  menuItemToAdd.parent = parentKey;
  let newItems = JSON.parse(JSON.stringify(items));
  newItems.splice(parentIndex + 1, 0, menuItemToAdd);

  // Update hasChildren on parent appropriately
  if (parentKey !== null) {
    newItems[parentIndex].hasChildren = true;
  }

  return newItems;
}

export function moveMenuItem(items, parentKey, key, moveWithChildren) {
  let index = items.findIndex((item) => key === item.key);
  if (index === -1) {
    return items;
  }
  let oldMenuItem = JSON.parse(JSON.stringify(items[index]));
  let menuItem = JSON.parse(JSON.stringify(items[index]));
  menuItem.parent = parentKey;
  let parentIndex =
    parentKey === null ? -1 : items.findIndex((item) => parentKey === item.key);
  let depthChange = 0;
  if (parentIndex === -1) {
    depthChange = 0 - menuItem.depth;
  } else {
    depthChange = items[parentIndex].depth + 1 - menuItem.depth;
  }
  menuItem.depth += depthChange;

  if (moveWithChildren) {
    // Move Item with it's children
    let newItems = JSON.parse(JSON.stringify(items));
    let childKeys = getChildKeys(items, key, false);
    let indexOfLastChild = index;
    childKeys.forEach((childKey) => {
      let i = newItems.findIndex((item) => childKey === item.key);
      if (i > indexOfLastChild) {
        indexOfLastChild = i;
      }
    });
    let movingItems = newItems.splice(index, indexOfLastChild - index + 1);
    movingItems.forEach((item, i) => {
      if (i === 0) {
        newItems.splice(parentIndex + i + 1, 0, menuItem);
      } else {
        newItems.splice(parentIndex + i + 1, 0, {
          ...item,
          depth: item.depth + depthChange,
        });
      }
    });

    // Update hasChildren on parent (old and new) appropriately
    if (parentKey !== null) {
      let newParentIndex = newItems.findIndex((item) => parentKey === item.key);
      if (numberOfChildren(newItems, parentKey) === 0) {
        newItems[newParentIndex].hasChildren = false;
      } else {
        newItems[newParentIndex].hasChildren = true;
      }
    }
    if (oldMenuItem.parent !== null) {
      let oldParentIndex = newItems.findIndex(
        (item) => oldMenuItem.parent === item.key
      );
      if (numberOfChildren(newItems, oldMenuItem.parent) === 0) {
        newItems[oldParentIndex].hasChildren = false;
      } else {
        newItems[oldParentIndex].hasChildren = true;
      }
    }
    return newItems;
  } else {
    // Move Item without it's children
    let newItems = removeMenuItem(items, key, true);
    menuItem.hasChildren = false;
    newItems = addMenuItem(newItems, parentKey, menuItem);
    return newItems;
  }
}

export function numberOfChildren(items, parentKey) {
  let children = items.filter((item) => parentKey === item.parent);
  return children.length;
}

export function getChildKeys(items, parentKey, shallow = false) {
  let newItems = JSON.parse(JSON.stringify(items));
  let children = [];
  newItems.forEach((item) => {
    if (item.parent === parentKey) {
      children.push(item.key);
    }
  });
  if (shallow) {
    return children;
  }
  for (var i = 0; i < children.length; i++) {
    for (var j = 0; j < newItems.length; j++) {
      if (children[i] === newItems[j].parent) {
        children.push(newItems[j].key);
      }
    }
  }
  return children;
}

export function getMenuItemByKey(items, key) {
  if (key === null || key === undefined) {
    return null;
  }
  return items.find((item) => key === item.key);
}

export function getValue(items, key, parameter) {
  if (key === null || key === undefined) {
    return null;
  }
  let item = items.find((item) => key === item.key);
  if (item) {
    return item[parameter];
  }
  return null;
}

export function updateValue(items, key, parameter, value) {
  let newItems = JSON.parse(JSON.stringify(items));
  if (key === null || key === undefined) {
    return null;
  }
  let index = newItems.findIndex((item) => item.key === key);
  if (index !== -1) {
    newItems[index][parameter] = value;
  }
  return newItems;
}

export function newMenuItem(depth = 0, parent = null) {
  return {
    name: "",
    url: "",
    key: uuid4().toString(),
    icon: "",
    target: "frame",
    isExpanded: false,
    disabled: false,
    forceAnchor: true,
    depth: depth,
    parent: parent,
    hasChildren: false,
  };
}
/*
  Iterative END
*/

/*
  Recursive START
*/
export function updateValueOnItemsRecursive(items, key, parameter, value) {
  let newItems = [];
  items.forEach((item) => {
    let newItem = JSON.parse(JSON.stringify(item));
    newItem.links = updateValueOnItemsRecursive(
      item.links,
      key,
      parameter,
      value
    );
    if (newItem.key === key) {
      newItem[parameter] = value;
    }
    newItems.push(newItem);
  });
  return newItems;
}

export function getMenuItemByKeyRecursive(items, key) {
  let index = items.findIndex((item) => key === item.key);
  if (index !== -1) {
    return items[index];
  }
  let returnItem = null;
  items.some((item) => {
    returnItem = getMenuItemByKeyRecursive(item.links, key);
    if (returnItem !== null) {
      return true;
    }
    return false;
  });
  return returnItem;
}

export function getParentKeyRecursive(items, key) {
  let index = items.findIndex((item) => key === item.key);
  if (index !== -1) {
    return null;
  }
  let parentKey = null;
  items.some((item) => {
    index = item.links.findIndex((link) => key === link.key);
    if (index !== -1) {
      parentKey = item.key;
      return true;
    }
    parentKey = getParentKeyRecursive(item.links, key);
    if (parentKey !== null) {
      return true;
    }
    return false;
  });
  return parentKey;
}

export function removeMenuItemRecursive(items, key) {
  let index = items.findIndex((item) => key === item.key);
  if (index !== -1) {
    return items.filter((item) => key !== item.key);
  }
  let newItems = [];
  items.forEach((item) => {
    let newItem = JSON.parse(JSON.stringify(item));
    newItem.links = removeMenuItemRecursive(newItem.links, key);
    newItems.push(newItem);
  });
  return newItems;
}

export function addMenuItemRecursive(items, parentKey, menuItem) {
  let newItems = [];
  if (parentKey === null) {
    newItems = newItems.concat(menuItem);
  }
  items.forEach((item) => {
    let newItem = JSON.parse(JSON.stringify(item));
    if (parentKey !== null) {
      newItem.links = addMenuItemRecursive(item.links, parentKey, menuItem);
    }
    if (newItem.key === parentKey) {
      newItem.links = [menuItem].concat(newItem.links);
    }
    newItems.push(newItem);
  });
  return newItems;
}

export function newMenuItemRecursive() {
  return {
    name: "",
    url: "",
    key: uuid4().toString(),
    icon: "",
    target: "frame",
    isExpanded: false,
    disabled: false,
    forceAnchor: true,
    links: [],
  };
}

/*
  Recursive END
*/
